import { Alert, Badge, Card, Group, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconMicrophone } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import type { FailoverHealthDto } from "@/types/ai-assistant-failover-health";
import { fetchFailoverHealth } from "./failover-health.api";
import { FailoverHistoryTable } from "./failover-history-table";
import {
	badgeView,
	costWarningLines,
	voiceWarningText,
} from "./failover-insights.logic";

/**
 * Visibilitas failover untuk admin: lencana status penyedia, spanduk biaya, peringatan
 * "suara tanpa cadangan", dan riwayat. Hanya membaca; breaker in-memory per instance
 * sehingga lencana bisa berbeda antar instance (kejadian di DB tetap sama).
 */
export function FailoverInsights() {
	const [health, setHealth] = useState<FailoverHealthDto | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const controller = new AbortController();
		fetchFailoverHealth(controller.signal)
			.then(setHealth)
			.catch((err: unknown) => {
				if (err instanceof Error && err.name === "AbortError") return;
				setError(err instanceof Error ? err.message : "Gagal memuat status");
			});
		return () => controller.abort();
	}, []);

	if (!health) {
		// `data-failover-error` menandai keadaan akhir "gagal memuat" supaya test
		// bisa menunggu kondisi, bukan menebak lamanya render dengan jeda tetap.
		return error ? (
			<Text size="sm" c="red" data-failover-error>
				{error}
			</Text>
		) : null;
	}

	const view = badgeView(health.badge, health.backupSlot);
	const cost = health.costWarning
		? costWarningLines(health.costWarning, health.backupSlot)
		: null;

	return (
		<Stack gap="sm" data-failover-insights>
			<Card withBorder padding="md" radius="md">
				<Stack gap="xs">
					<Group gap="sm">
						<Text fw={600}>Status penyedia</Text>
						<Badge color={view.color} data-provider-badge={health.badge.state}>
							{view.label}
						</Badge>
					</Group>
					<Text size="sm" c="dimmed">
						{view.detail}
					</Text>
					<FailoverHistoryTable reloadWhen={health.generatedAt} />
				</Stack>
			</Card>
			{cost && (
				<Alert
					color="orange"
					icon={<IconAlertTriangle size={16} />}
					title={cost.title}
					data-cost-warning
				>
					{cost.lines.map((line) => (
						<Text key={line} size="sm">
							{line}
						</Text>
					))}
				</Alert>
			)}
			{health.voiceWarnings.map((kind) => (
				<Alert
					key={kind}
					color="yellow"
					icon={<IconMicrophone size={16} />}
					data-voice-warning={kind}
				>
					{voiceWarningText(kind)}
				</Alert>
			))}
		</Stack>
	);
}
